import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./ReservationDetails.module.scss";
import { API_ENDPOINTS, getAuthHeaders } from "../../config/api";
import { dateDiffInNights } from "../../utils/dateDiff";

const STATUS_OPTIONS = [
  "pending",
  "quoted",
  "confirmed",
  "checked-in",
  "checked-out",
  "cancelled",
  "denied",
];

const STATUS_LABELS = {
  pending: "En attente",
  quoted: "Devis envoyé",
  confirmed: "Confirmée",
  "checked-in": "Arrivée",
  "checked-out": "Départ",
  cancelled: "Annulée",
  denied: "Refusée",
};

const ROOM_LABELS = {
  "economy-single": "Simple Économique",
  "superior-single": "Simple Supérieure",
  "economy-double": "Double Économique",
  "superior-double": "Double Supérieure",
  triple: "Triple",
  suite: "Suite",
};

const CURRENCY_SYMBOLS = { TND: "TND", EUR: "€", USD: "$" };

function ReservationDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [status, setStatus] = useState("pending");
  const [currency, setCurrency] = useState("TND");
  const [language, setLanguage] = useState("fr");
  const [confirmLanguage, setConfirmLanguage] = useState("fr");
  const [unavailableLanguage, setUnavailableLanguage] = useState("fr");
  const [adminNotes, setAdminNotes] = useState("");
  const [unavailableNotes, setUnavailableNotes] = useState("");
  const [roomPrices, setRoomPrices] = useState({});

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);

  const nights = useMemo(() => {
    if (!reservation) return 0;
    return dateDiffInNights(reservation.checkIn, reservation.checkOut);
  }, [reservation]);

  const totalCost = useMemo(() => {
    const total = Object.values(roomPrices).reduce((sum, price) => sum + (price * nights), 0);
    return Number.isFinite(total) ? total : 0;
  }, [roomPrices, nights]);

  const initializeActionState = (data) => {
    setStatus(data.status || "pending");
    setCurrency(data.currency || "TND");
    setLanguage(data.preferredLanguage || "fr");
    setConfirmLanguage(data.preferredLanguage || "fr");
    setUnavailableLanguage(data.preferredLanguage || "fr");
    setAdminNotes(data.adminNotes || "");
    setUnavailableNotes("");

    const prices = {};
    (data.rooms || []).forEach((room, index) => {
      prices[index] = room.pricePerNight || 0;
    });
    setRoomPrices(prices);
  };

  const readErrorMessage = async (response, fallback) => {
    try {
      const data = await response.json();
      return data.error || data.message || fallback;
    } catch {
      return fallback;
    }
  };

  const fetchReservation = useCallback(async () => {
    setLoading(true);
    setError("");
    setActionError("");

    try {
      const response = await fetch(`${API_ENDPOINTS.RESERVATIONS}/${id}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Échec du chargement de la réservation."));
      }

      const data = await response.json();
      setReservation(data);
      initializeActionState(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "Échec du chargement de la réservation.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReservation();
  }, [fetchReservation]);

  const mapRoomsWithPricing = () => {
    if (!reservation) return [];
    return reservation.rooms.map((room, index) => ({
      ...room,
      pricePerNight: roomPrices[index] || 0,
    }));
  };

  const formatCurrency = (amount, curr) => `${CURRENCY_SYMBOLS[curr] || curr} ${Number(amount || 0).toFixed(2)}`;

  const previewQuote = async () => {
    if (!reservation) return;
    setActionError("");
    setPreviewLoading(true);

    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservation._id}/preview-quote`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            rooms: mapRoomsWithPricing(),
            currency,
            totalCost,
            language,
            adminNotes,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Échec de l'aperçu du devis."));
      }

      const data = await response.json();
      setPreviewContent({ ...data, type: "quote" });
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Échec de l'aperçu du devis.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendQuote = async () => {
    if (!reservation) return;
    if (!window.confirm("Envoyer le devis au client ?")) return;

    setIsSaving(true);
    setActionError("");

    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservation._id}/send-quote`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            rooms: mapRoomsWithPricing(),
            currency,
            totalCost,
            language,
            adminNotes,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Échec de l'envoi du devis."));
      }

      await fetchReservation();
      alert("✅ Devis envoyé avec succès.");
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Échec de l'envoi du devis.");
    } finally {
      setIsSaving(false);
    }
  };

  const previewConfirmation = async () => {
    if (!reservation) return;
    setActionError("");
    setPreviewLoading(true);

    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservation._id}/preview-confirmation`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ language: confirmLanguage }),
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Échec de l'aperçu de confirmation."));
      }

      const data = await response.json();
      setPreviewContent({ ...data, type: "confirmation" });
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Échec de l'aperçu de confirmation.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmReservation = async () => {
    if (!reservation) return;
    if (!window.confirm("Confirmer cette réservation ?")) return;

    setIsSaving(true);
    setActionError("");

    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservation._id}/confirm`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ language: confirmLanguage }),
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Échec de la confirmation."));
      }

      await fetchReservation();
      alert("✅ Réservation confirmée et email envoyé.");
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Échec de la confirmation.");
    } finally {
      setIsSaving(false);
    }
  };

  const previewUnavailable = async () => {
    if (!reservation) return;
    setActionError("");
    setPreviewLoading(true);

    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservation._id}/preview-unavailable`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ language: unavailableLanguage }),
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Échec de l'aperçu d'indisponibilité."));
      }

      const data = await response.json();
      setPreviewContent({ ...data, type: "unavailable" });
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Échec de l'aperçu d'indisponibilité.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendUnavailable = async () => {
    if (!reservation) return;
    if (!window.confirm("Envoyer la notification d'indisponibilité ?")) return;

    setIsSaving(true);
    setActionError("");

    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservation._id}/send-unavailable`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            language: unavailableLanguage,
            adminNotes: unavailableNotes,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Échec de l'envoi de la notification."));
      }

      await fetchReservation();
      alert("✅ Notification d'indisponibilité envoyée.");
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Échec de l'envoi de la notification.");
    } finally {
      setIsSaving(false);
    }
  };

  const updateStatus = async () => {
    if (!reservation) return;
    setIsSaving(true);
    setActionError("");

    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservation._id}/status`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ status }),
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Échec de la mise à jour du statut."));
      }

      await fetchReservation();
      alert("✅ Statut mis à jour.");
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Échec de la mise à jour du statut.");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteReservation = async () => {
    if (!reservation) return;
    if (!window.confirm("Supprimer définitivement cette réservation ?")) return;

    setIsSaving(true);
    setActionError("");

    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservation._id}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Échec de la suppression."));
      }

      alert("✅ Réservation supprimée.");
      navigate("/dashboard/reservations");
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Échec de la suppression.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.stateBlock}>
        <div className={styles.spinner}></div>
        <p>Chargement de la réservation...</p>
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className={styles.stateBlock}>
        <p>{error || "Réservation introuvable."}</p>
        <button className={styles.secondaryBtn} onClick={() => navigate("/dashboard/reservations")}>
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.secondaryBtn} onClick={() => navigate("/dashboard/reservations")}>
          ← Retour aux réservations
        </button>
        <div>
          <h2>Réservation {reservation.confirmationCode}</h2>
          <p>
            {reservation.firstName} {reservation.lastName} • {new Date(reservation.createdAt).toLocaleString("fr-FR")}
          </p>
        </div>
      </div>

      {actionError && <div className={styles.errorBanner}>{actionError}</div>}

      <div className={styles.layout}>
        <section className={styles.detailsCard}>
          <h3>Détails</h3>
          <div className={styles.detailGrid}>
            <div><strong>Client</strong><span>{reservation.firstName} {reservation.lastName}</span></div>
            <div><strong>Email</strong><span>{reservation.email}</span></div>
            <div><strong>Téléphone</strong><span>{reservation.phone || "N/A"}</span></div>
            <div><strong>Langue</strong><span>{(reservation.preferredLanguage || "en").toUpperCase()}</span></div>
            <div><strong>Arrivée</strong><span>{new Date(reservation.checkIn).toLocaleDateString("fr-FR")}</span></div>
            <div><strong>Départ</strong><span>{new Date(reservation.checkOut).toLocaleDateString("fr-FR")}</span></div>
            <div><strong>Nuits</strong><span>{nights}</span></div>
            <div><strong>Statut</strong><span>{STATUS_LABELS[reservation.status] || reservation.status}</span></div>
            <div><strong>Devise</strong><span>{reservation.currency || "TND"}</span></div>
            <div><strong>Coût total</strong><span>{formatCurrency(reservation.totalCost, reservation.currency || "TND")}</span></div>
          </div>

          <div className={styles.section}>
            <h4>Chambres</h4>
            {(reservation.rooms || []).map((room, index) => (
              <div key={index} className={styles.roomItem}>
                <p><strong>{ROOM_LABELS[room.roomType] || room.roomType}</strong></p>
                <p>
                  {room.adults} adulte(s)
                  {room.childUnder2 > 0 && `, ${room.childUnder2} <2`}
                  {room.child2to12 > 0 && `, ${room.child2to12} 2-12`}
                  {room.child12to18 > 0 && `, ${room.child12to18} 12-18`}
                </p>
                {room.pricePerNight > 0 && (
                  <p>Prix/nuit: {formatCurrency(room.pricePerNight, reservation.currency || "TND")}</p>
                )}
              </div>
            ))}
          </div>

          {reservation.requests && (
            <div className={styles.section}>
              <h4>Demandes spéciales</h4>
              <p>{reservation.requests}</p>
            </div>
          )}

          {reservation.emailsSent && reservation.emailsSent.length > 0 && (
            <div className={styles.section}>
              <h4>Historique emails</h4>
              <ul className={styles.historyList}>
                {reservation.emailsSent.map((email, index) => (
                  <li key={index}>
                    {email.type} ({(email.language || "en").toUpperCase()}) • {new Date(email.sentAt).toLocaleString("fr-FR")}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className={styles.actionsCard}>
          <h3>Actions</h3>

          <div className={styles.actionBlock}>
            <h4>Mise à jour du statut</h4>
            <div className={styles.inlineRow}>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {STATUS_LABELS[option] || option}
                  </option>
                ))}
              </select>
              <button className={styles.primaryBtn} onClick={updateStatus} disabled={isSaving}>
                Enregistrer statut
              </button>
            </div>
          </div>

          <div className={styles.actionBlock}>
            <h4>Devis</h4>
            <div className={styles.twoCols}>
              <label>
                Langue
                <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
              </label>
              <label>
                Devise
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  <option value="TND">TND</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </label>
            </div>

            <div className={styles.priceList}>
              {(reservation.rooms || []).map((room, index) => (
                <label key={index}>
                  {ROOM_LABELS[room.roomType] || room.roomType} (prix / nuit)
                  <input
                    type="number"
                    min="0"
                    value={roomPrices[index] || 0}
                    onChange={(e) =>
                      setRoomPrices((prev) => ({
                        ...prev,
                        [index]: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </label>
              ))}
            </div>

            <p className={styles.totalLine}>Total calculé: <strong>{formatCurrency(totalCost, currency)}</strong></p>

            <label>
              Notes client (optionnel)
              <textarea
                rows={3}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
              />
            </label>

            <div className={styles.inlineRow}>
              <button className={styles.secondaryBtn} onClick={previewQuote} disabled={previewLoading || isSaving}>
                Aperçu devis
              </button>
              <button className={styles.primaryBtn} onClick={sendQuote} disabled={isSaving}>
                Envoyer devis
              </button>
            </div>
          </div>

          <div className={styles.actionBlock}>
            <h4>Confirmation</h4>
            <label>
              Langue
              <select value={confirmLanguage} onChange={(e) => setConfirmLanguage(e.target.value)}>
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </select>
            </label>
            <div className={styles.inlineRow}>
              <button className={styles.secondaryBtn} onClick={previewConfirmation} disabled={previewLoading || isSaving}>
                Aperçu confirmation
              </button>
              <button className={styles.primaryBtn} onClick={confirmReservation} disabled={isSaving}>
                Confirmer et envoyer
              </button>
            </div>
          </div>

          <div className={styles.actionBlock}>
            <h4>Indisponibilité</h4>
            <label>
              Langue
              <select value={unavailableLanguage} onChange={(e) => setUnavailableLanguage(e.target.value)}>
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </select>
            </label>
            <label>
              Notes client (optionnel)
              <textarea
                rows={3}
                value={unavailableNotes}
                onChange={(e) => setUnavailableNotes(e.target.value)}
              />
            </label>
            <div className={styles.inlineRow}>
              <button className={styles.secondaryBtn} onClick={previewUnavailable} disabled={previewLoading || isSaving}>
                Aperçu indisponibilité
              </button>
              <button className={styles.warningBtn} onClick={sendUnavailable} disabled={isSaving}>
                Envoyer indisponibilité
              </button>
            </div>
          </div>

          <div className={styles.actionBlock}>
            <h4>Suppression</h4>
            <button className={styles.dangerBtn} onClick={deleteReservation} disabled={isSaving}>
              Supprimer la réservation
            </button>
          </div>
        </section>
      </div>

      {previewContent && (
        <section className={styles.previewCard}>
          <div className={styles.previewHeader}>
            <h3>
              Aperçu email: {previewContent.type} ({(previewContent.language || "en").toUpperCase()})
            </h3>
            <button className={styles.secondaryBtn} onClick={() => setPreviewContent(null)}>
              Fermer
            </button>
          </div>
          <p><strong>Sujet:</strong> {previewContent.subject}</p>
          <div
            className={styles.previewHtml}
            dangerouslySetInnerHTML={{ __html: previewContent.html }}
          />
        </section>
      )}
    </div>
  );
}

export default ReservationDetails;
